import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { sagaApiGen } from 'utils/sagaApiGeneratorHelpers';
import { conversationsOverviewActions } from '../../conversationsOverview/actions/conversationsOverview';
import { notificationActions } from '../../notifications/actions/notification';
import { getRequestID } from '../../shared/util/request';
import actions from '../actions/bulkActions';
import { bulkActionApi } from '../api/bulkActions';
import { getBulkConversationIDs, getInternalNote, getReplyMessage, getSelectedActions, getSelectedQueue, getSelectedTagNamesToAdd, getSelectedTagNamesToRemove, } from '../selectors/bulkActions';
const { bulkActionsStart, bulkActionsCancel, bulkActionsCloseModal, bulkActionsCallApi, bulkActionsCallApiSucceeded, bulkActionsCallApiFailed, bulkActionsProcessingEventStarted, bulkActionsProcessingEventFinished, } = actions;
const { conversationsOverviewSelectionClear } = conversationsOverviewActions;
export const createStartBulkNotificationAction = (id, title, csids, payload) => {
    return notificationActions.notificationAdd({
        id,
        title,
        text: `${csids.length} ${csids.length === 1 ? 'conversation' : 'conversations'}`,
        icon: 'i-info',
        time: 10000,
        rightAction: {
            text: 'Cancel',
            action: function* () {
                yield put(notificationActions.notificationRemoveAllById({ id }));
                yield put(bulkActionsCancel());
            },
        },
        actionOnTimeout: (dispatch) => {
            dispatch(notificationActions.notificationRemoveAllById({ id }));
            dispatch({
                type: bulkActionsCallApi.toString(),
                payload,
            });
        },
        closeAction: function* () {
            yield put(notificationActions.notificationRemoveAllById({
                id,
            }));
            yield put({
                type: bulkActionsCallApi.toString(),
                payload,
            });
        },
    });
};
function* showAddTagNotification(csids) {
    const state = yield select();
    const id = getRequestID();
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Add tag', csids, {
        bulkType: 'addTag',
        data: {
            csids,
            tag_names: getSelectedTagNamesToAdd(state),
        },
    }));
}
function* showRemoveTagNotification(csids) {
    const state = yield select();
    const id = getRequestID();
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Remove tag', csids, {
        bulkType: 'removeTag',
        data: {
            csids,
            tag_names: getSelectedTagNamesToRemove(state),
        },
    }));
}
function* showCloseNotification(csids) {
    const id = getRequestID();
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Close', csids, {
        bulkType: 'close',
        data: {
            csids,
        },
    }));
}
function* showSendReplyNotification(csids) {
    const id = getRequestID();
    const state = yield select();
    const { htmlText, plainText } = getReplyMessage(state);
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Send reply', csids, {
        bulkType: 'sendReply',
        data: {
            csids,
            plain_text: plainText,
            html_text: htmlText,
        },
    }));
}
function* showTransferToQueueNotification(csids) {
    const id = getRequestID();
    const state = yield select();
    const queue = getSelectedQueue(state);
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Transfer to queue', csids, {
        bulkType: 'transferToQueue',
        data: {
            csids,
            new_queue_id: queue === null || queue === void 0 ? void 0 : queue.id,
        },
    }));
}
function* showAddInternalNoteNotification(csids) {
    const id = getRequestID();
    const state = yield select();
    const note = getInternalNote(state);
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Add internal note', csids, {
        bulkType: 'addInternalNote',
        data: {
            csids,
            message: note,
        },
    }));
}
function* showCancelCSATNotification(csids) {
    const id = getRequestID();
    yield put(createStartBulkNotificationAction(id, 'Bulk action: Cancel CSAT', csids, {
        bulkType: 'cancelCSAT',
        data: {
            csids,
        },
    }));
}
function* bulkActionFailed(action) {
    const { payload: error } = action;
    console.log('bulkActionFailed', error);
    yield put(notificationActions.notificationAdd({
        title: 'Could not start bulk action.',
        text: error.message,
        icon: 'i-exclamation',
        status: 'danger',
    }));
}
function* bulkActionProcessingStarted(action) {
    const { payload: { bulk_id: bulkId, subject: title, message: text }, } = action;
    yield put(notificationActions.notificationAdd({
        id: bulkId,
        title,
        text,
        icon: 'i-sync',
    }));
}
function* bulkActionProcessingFinished(action) {
    const { payload: { bulk_id: bulkId, subject: title, message: text, number_failed_actions: numberOfFailedActions, total_conversations: totalNumberOfConversations, }, } = action;
    yield put(notificationActions.notificationRemoveAllById({ id: bulkId }));
    const notificationOptions = {
        icon: 'exclamation',
        time: 5000,
    };
    if (numberOfFailedActions === 0) {
        // Complete success
        notificationOptions.icon = 'done';
        notificationOptions.status = 'success';
    }
    else if (numberOfFailedActions === totalNumberOfConversations) {
        // Complete fail
        notificationOptions.status = 'danger';
    }
    else {
        // Partial success/fail
        notificationOptions.status = 'warning';
    }
    yield put(notificationActions.notificationAdd(Object.assign({ id: bulkId, title,
        text }, notificationOptions)));
}
export const bulkActions = [
    takeLatest(bulkActionsStart.toString(), function* () {
        const state = yield select();
        const csids = getBulkConversationIDs(state);
        const selectedActions = getSelectedActions(state);
        for (let i = 0; i < selectedActions.length; i++) {
            switch (selectedActions[i]) {
                case 'addTag':
                    yield showAddTagNotification(csids);
                    break;
                case 'removeTag':
                    yield showRemoveTagNotification(csids);
                    break;
                case 'close':
                    yield showCloseNotification(csids);
                    break;
                case 'sendReply':
                    yield showSendReplyNotification(csids);
                    break;
                case 'transferToQueue':
                    yield showTransferToQueueNotification(csids);
                    break;
                case 'addInternalNote':
                    yield showAddInternalNoteNotification(csids);
                    break;
                case 'cancelCSAT':
                    yield showCancelCSATNotification(csids);
                    break;
            }
        }
        yield put(conversationsOverviewSelectionClear());
        yield put(bulkActionsCloseModal());
    }),
    takeEvery(bulkActionsCallApi.toString(), sagaApiGen(bulkActionApi, bulkActionsCallApiSucceeded, bulkActionsCallApiFailed)),
    takeLatest(bulkActionsProcessingEventStarted.toString(), bulkActionProcessingStarted),
    takeLatest(bulkActionsProcessingEventFinished.toString(), bulkActionProcessingFinished),
    takeLatest(bulkActionsCallApiFailed.toString(), bulkActionFailed),
];
